import { useMemo } from 'react';
import endpoints from '@services/api/endpoints';
import useFetch from '@services/api/useFetch';
import { replaceUrlParams } from '@utils/string-util';

import type {
  EventLeadFeedbackRequest,
  EventLeadItem,
  EventLeadPersonalized,
  EventLeadSearchParams,
  EventLeadSearchResults,
  EventLeadTicket,
} from '../../../types/events/leads';

export const useEventLeadsApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async getPersonalized(id: string): Promise<EventLeadPersonalized> {
        return api.get(
          replaceUrlParams(endpoints.eventLeads.getPersonalized, {
            id,
          })
        );
      },
      async search(
        organizationId: string,
        params: Partial<EventLeadSearchParams>
      ): Promise<EventLeadSearchResults> {
        return api.get(endpoints.eventLeads.search, {
          organizationId,
          ...params,
        });
      },
      async isRunningTicket(organizationId: string): Promise<EventLeadTicket> {
        return api.get(
          replaceUrlParams(endpoints.eventLeads.isRunning, { organizationId }),
          {
            organizationId,
          }
        );
      },
      async togglePin(id: string, pinned: boolean): Promise<EventLeadItem> {
        return api.patch(
          replaceUrlParams(endpoints.eventLeads.togglePin, {
            id,
            pinned,
          })
        );
      },
      async addFeedback(
        id: string,
        params: EventLeadFeedbackRequest
      ): Promise<EventLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.eventLeads.updateFeedback, {
            id,
          }),
          params
        );
      },
      async updateNotes(
        id: string,
        notes: string
      ): Promise<EventLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.eventLeads.updateNotes, {
            id,
          }),
          {
            notes,
          }
        );
      },
    };
  }, [api]);
};

export const eventLeadsApiKeys = {
  all: ['event-leads'] as const,
  getPersonalized: (id: string) =>
    [...eventLeadsApiKeys.all, 'get-personalized', id] as const,
  isRunningTicket: (orgId: string) =>
    [...eventLeadsApiKeys.all, 'is-running-ticket', orgId] as const,
  search: (orgId: string, params?: Partial<EventLeadSearchParams>) =>
    [...eventLeadsApiKeys.all, 'event-leads-search', orgId, params].filter(
      Boolean
    ),
};
